import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Lock, CreditCard, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { API } from '../../lib/constants';
import { formatApiErrorDetail } from '../../context/AuthContext';

// MOCK Razorpay checkout modal. Replace with window.Razorpay(options).open() when live keys are added.
export const MockCheckoutDialog = ({ checkout, onClose, onPaid }) => {
  const [paying, setPaying] = useState(false);

  const pay = async (fail = false) => {
    setPaying(true);
    await new Promise((r) => setTimeout(r, 1400));
    try {
      const paymentId = fail ? 'failed_mock' : `pay_mock_${Math.random().toString(36).slice(2, 14)}`;
      await axios.post(`${API}/billing/verify`, { order_id: checkout.order_id, payment_id: paymentId, signature: 'mock_signature' });
      onPaid();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const amount = checkout ? `₹${(checkout.amount / 100).toLocaleString('en-IN')}` : '';

  return (
    <Dialog open={!!checkout} onOpenChange={(o) => !o && !paying && onClose()}>
      <DialogContent className="p-0 overflow-hidden max-w-sm bg-surface border-2 border-cyan rounded-none shadow-[0_18px_40px_rgba(40,35,52,0.10)] [&>button]:hidden" data-testid="mock-checkout-dialog">
        {checkout && (
          <>
            <div className="bg-[#282334] text-white p-5 relative">
              <button onClick={onClose} disabled={paying} className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="checkout-close-btn"><X className="w-4 h-4" /></button>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-white/15 flex items-center justify-center font-display font-bold">{checkout.name.charAt(0)}</span>
                <div>
                  <p className="font-semibold text-sm">{checkout.name}</p>
                  <p className="text-xs text-white/60">{checkout.description}</p>
                </div>
              </div>
              <p className="text-3xl font-extrabold tracking-tight mt-5" data-testid="checkout-amount">{amount}</p>
              <p className="font-mono text-[11px] text-white/50 mt-1">Order {checkout.order_id}</p>
            </div>
            <div className="p-5 space-y-4 bg-surface">
              <div className="space-y-2">
                <p className="label">Card details (test)</p>
                <div className="border-2 border-line divide-y-2 divide-line font-mono text-sm text-slate-400">
                  <div className="px-3 py-2.5 flex items-center gap-2"><CreditCard className="w-4 h-4" /> 4111 1111 1111 1111</div>
                  <div className="grid grid-cols-2 divide-x-2 divide-line">
                    <div className="px-3 py-2.5">12 / 29</div>
                    <div className="px-3 py-2.5">CVV •••</div>
                  </div>
                </div>
                <p className="font-mono text-[11px] text-slate-500">{checkout.prefill?.email}</p>
              </div>
              <button data-testid="checkout-pay-btn" onClick={() => pay(false)} disabled={paying} className="w-full inline-flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wider text-sm px-4 py-3.5 text-white bg-[#3395ff] border-2 border-[#3395ff] shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-sm transition-all disabled:opacity-60">
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {paying ? 'Processing…' : `Pay ${amount}`}
              </button>
              <div className="flex items-center justify-between font-mono text-[10px] text-slate-500">
                <span>Secured by <span className="font-bold text-white">Razorpay</span> · test mode</span>
                <button data-testid="checkout-simulate-fail-btn" onClick={() => pay(true)} disabled={paying} className="hover:text-red-500 underline-offset-2 hover:underline">Simulate failure</button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
